<template>
    <div class="d-flex split-input">
        <x-currency-text-field
            :value="amount"
            class="value-percent-amount"
            :class="{
                'input-no-margin': noMargin,
                'input-small': small,
                'required': required,
            }"
            :label="amountLabel"
            :placeholder="amountPlaceholder"
            :prefix="currencyCode"
            :rules="[
                () => required ? ruleRequired(amount, amountLabel) : true,
                () => allowZero ? true : ruleGreaterThanOrEqualTo(amount, 1, amountLabel),
                () => amount && !calculatePercentageWithoutLimit ? ruleLessThanOrEqualTo(amount.toFixed(2), parseFloat(actualMaxAmount).toFixed(2), amountLabel) : true
            ]"
            :disabled="isDisabled"
            :readonly="readonly"
            :hide-details="hideDetails"
            outline
            @input="setAmount"
        />

        <span
            class="value-percent-divider"
            :class="{
                'no-margin': noMargin,
                'small': small
            }"
        >/</span>

        <x-text-field
            :value="displayPercentage"
            class="value-percent-percentage input-text-right"
            :class="{
                'input-no-margin': noMargin,
                'input-small': small,
                'required': required && Boolean(percentageLabel) && percentageLabel !== ' ',
            }"
            :label="percentageLabel"
            :placeholder="percentagePlaceholder"
            suffix="%"
            :rules="[
                v => required ? ruleRequired(v, 'Percentage') : true,
                v => allowZero ? true : ruleGreaterThan(v, 0, 'Percentage'),
                v => !calculatePercentageWithoutLimit ? ruleLessThanOrEqualTo(v, 100, 'Percentage') : true,
                ...percentageRules
            ]"
            :disabled="isDisabled"
            :readonly="readonly"
            :hide-details="hideDetails"
            outline
            @input="setPercentage"
            @focus="setHasFocus(true)"
            @blur="setHasFocus(false)"
        />

        <x-btn flat icon color="indigo" @click="editing = !editing" v-show="isEditable">
            <x-icon>edit</x-icon>
        </x-btn>
    </div>
</template>

<script>
    import rules from '~/mixins/rules';
    import Big from 'big.js'

    export default {
        name: 'ValuePercentSplitInput',
        mixins: [rules],
        props: {
            value: {},
            amountValue: {},
            required: {
                type: Boolean
            },
            allowZero: {
                type: Boolean,
                default: false
            },
            bpsAmount: {
                type: Boolean,
                default: false
            },
            amountLabel: {
                type: String
            },
            amountPlaceholder: {
                type: String
            },
            percentageLabel: {
                type: String
            },
            percentagePlaceholder: {
                type: String
            },
            total: {
                type: [Number, String],
            },
            maxAmount: {
                type: Number
            },
            currencyCode: {
                type: String,
                default: 'USD'
            },
            isEditable: {
                type: Boolean,
                default: false
            },
            percentageDecimalPlaces: {
                type: Number,
                default: 12
            },
            hideDetails: {
                type: Boolean
            },
            noMargin: {
                type: Boolean
            },
            small: {
                type: Boolean
            },
            readonly: {
                type: Boolean
            },
            percentageRules: {
                type: Array,
                default: () => []
            },
            calculatePercentageWithoutLimit: {
                type: Boolean,
                default: false
            },
        },
        data() {
            return {
                editing: false,
                amount: 0,
                percentage: 0,
                percentHasFocus: false,
            }
        },
        computed: {
            actualMaxAmount() {
                return this.maxAmount || this.total || 0;
            },
            isDisabled() {
                if (this.isEditable === false) {
                    return false;
                }

                return !this.editing;
            },
            displayPercentage() {
                if (this.percentHasFocus === false && this.isNumber(this.percentage)) {
                    return this.round(this.percentage, 2);
                }

                return this.percentage;
            }
        },
        mounted () {
            this.percentage = this.value;
            this.amount = this.calculateAmount(this.total, this.percentage);
        },
        watch: {
            value(newValue) {
                this.setPercentage(newValue)
            },
            total (total) {
                this.amount = this.calculateAmount(total, this.percentage);
            },
            percentage() {
                this.$emit('update:amount-value', this.amount);
            }
        },
        methods: {
            calculatePercentage(total, amount) {
                if (!this.isNumber(total) || total === 0 || !this.isNumber(amount)) {
                    return '';
                }

                return this.round(Big(Big(amount).div(total)).times(100));
            },
            calculateAmount(total, percentage) {
                if (!this.isNumber(total) || !this.isNumber(percentage)) {
                    return '';
                }

                return Number(Big(total).times(Big(percentage).div(100)));
            },
            round(value, decimalPlaces = this.percentageDecimalPlaces) {
                value = parseFloat(value);

                if (parseFloat(value.toFixed(decimalPlaces)) !== value && !isNaN(value)) {
                    return value.toFixed(decimalPlaces);
                }

                return value;
            },
            isNumber(n) {
                return (Boolean(n) || n === 0) && !isNaN(n);
            },
            setAmount (amount) {
                this.amount = amount;
                this.percentage = this.calculatePercentage(this.total, amount);
                this.$emit('input', this.percentage);
            },
            setPercentage (percentage) {
                this.percentage = this.isNumber(percentage) ? this.round(percentage) : percentage;
                this.$emit('input', this.percentage);

                this.amount = this.bpsAmount ? Math.ceil(this.calculateAmount(this.total, this.percentage)) : this.calculateAmount(this.total, this.percentage);
            },
            setHasFocus (focusState) {
                this.percentHasFocus = focusState;
                this.setPercentage(this.percentage);
            }
        }
    }
</script>

<style lang="scss" scoped>
    .value-percent-amount {
        width: 67%;
    }

    .value-percent-divider {
        font-size: rem(32px);
        margin: 20px 8px 0 8px;
        color: rgba(255, 255, 255, 0.5);
        line-height: 44px;

        &.no-margin {
            margin-top: 0;
        }
    }

    .value-percent-percentage {
        width: 33%;
    }
</style>
